import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { fabric } from 'fabric';
import { untilDestroyed } from 'ngx-take-until-destroy';
import {
  hasConsentSignatureAnswer,
  shouldShowConsentPdfNotAnswered
} from 'src/app/core/api/consent-form.util';
import { FormFieldConfig, SignatureConfig } from '../../../form-template-model';
import { FormTemplateGeneratorService } from '../../form-template-generator.service';

export interface FormTemplateSignatureValue {
  signatureType: 'drawn' | 'typed' | 'upload';
  drawnSignature?: string;
  typedSignature?: string;
  uploadedSignature?: string;
}

@Component({
  selector: 'csi-form-template-signature-field-generator',
  templateUrl: './form-template-signature-field-generator.component.html',
  styleUrls: ['./form-template-signature-field-generator.component.css']
})
export class FormTemplateSignatureFieldGeneratorComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: FormFieldConfig & { typeConfig: SignatureConfig };
  @Input() control: FormControl;
  @Input() formTemplateFieldKey: string;
  @Input() readOnly: boolean;
  @Input() formatForPDF: boolean;
  @Input() isConsentForm: boolean = false;
  @Input() supportingFields: { [key: string]: FormFieldConfig };

  @ViewChild('fabricSurface', { static: false }) canvasEl: ElementRef<HTMLCanvasElement>;

  public selectedTab = 0;
  public typedSignatureControl = new FormControl('');
  public typedOnly = false;
  public drawnSignatureDataUrl: string = null;
  /** Consent PDF: show when optional signature was left blank. */
  public showNotAnswered = false;
  /** Precomputed for template (avoid getters in HTML). */
  public displaySignatureData: any = null;
  public displayTypedSignature = '';
  private canvas: fabric.Canvas;
  private canvasInitTimeout: any = null;
  private readonly CANVAS_WIDTH = 400;
  private readonly CANVAS_HEIGHT = 120;

  constructor(
    private formTemplateGeneratorService: FormTemplateGeneratorService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.typedOnly = !!(
      this.config &&
      this.config.typeConfig &&
      this.config.typeConfig.typedOnly
    );
    if (this.typedOnly) {
      this.selectedTab = 1;
    }
    const existing = this.control && this.control.value;
    if (existing) {
      if (existing.signatureType === 'typed') {
        this.selectedTab = 1;
        this.typedSignatureControl.setValue(existing.typedSignature || '', { emitEvent: false });
      } else if (existing.signatureType === 'drawn') {
        this.selectedTab = 0;
      }
    }

    this.typedSignatureControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.selectedTab === 1 || this.typedOnly) {
      this.commitTypedSignature();
      }
    });

    this.refreshDisplayFields(existing);

    // Clinic modal (readOnly) and PDF both need a PNG — csi-signature-display often gets
    // zero width inside mat-stepper/dialog and also triggers Fabric wheel-listener warnings.
    if (this.formatForPDF || this.readOnly) {
      const rasterizePromise = this.rasterizeDrawnSignatureForDisplay();
      if (this.formatForPDF && rasterizePromise) {
        this.formTemplateGeneratorService.registerPdfRasterizePromise(rasterizePromise);
      }
    }

    this.showNotAnswered = shouldShowConsentPdfNotAnswered(
      this.isConsentForm,
      this.formatForPDF,
      this.hasSignatureValue(existing)
    );
  }

  private refreshDisplayFields(value: FormTemplateSignatureValue) {
    this.displayTypedSignature = (value && value.typedSignature) || '';
    this.displaySignatureData = null;
    if (value && value.signatureType === 'drawn' && value.drawnSignature) {
      try {
        this.displaySignatureData =
          typeof value.drawnSignature === 'string'
            ? JSON.parse(value.drawnSignature)
            : value.drawnSignature;
      } catch (e) {
        this.displaySignatureData = null;
      }
    }
  }

  private hasSignatureValue(value: FormTemplateSignatureValue): boolean {
    return hasConsentSignatureAnswer(value);
  }

  ngAfterViewInit() {
    if (!this.readOnly && !this.formatForPDF && !this.typedOnly) {
      if (this.canvasInitTimeout) {
        clearTimeout(this.canvasInitTimeout);
      }
      this.canvasInitTimeout = setTimeout(() => this.initializeCanvas());
    }
  }

  ngOnDestroy() {
    if (this.canvasInitTimeout) {
      clearTimeout(this.canvasInitTimeout);
      this.canvasInitTimeout = null;
    }
    if (this.canvas) {
      this.canvas.dispose();
      this.canvas = null;
    }
  }

  onTabChange(index: number) {
    this.selectedTab = index;
    if (index === 0) {
      this.ensureCanvasAndCommitDrawn();
    } else if (index === 1) {
      this.commitTypedSignature();
    }
  }

  private ensureCanvasAndCommitDrawn() {
    if (this.canvas) {
      this.commitDrawnSignature();
      return;
    }
      if (this.canvasInitTimeout) {
        clearTimeout(this.canvasInitTimeout);
      }
    this.canvasInitTimeout = setTimeout(() => {
      this.initializeCanvas();
      this.commitDrawnSignature();
    });
    }

  commitDrawnSignature() {
    if (this.readOnly || this.formatForPDF) {
      return;
    }
    const json =
      this.canvas && this.canvas.getObjects().length > 0
        ? JSON.stringify(this.canvas.toJSON())
        : null;

    this.updateControlValue({
      signatureType: 'drawn',
      drawnSignature: json,
      typedSignature: null
    });
  }

  clearDrawn() {
    if (this.canvas) {
      this.canvas.clear();
      this.canvas.backgroundColor = '#ffffff';
      this.canvas.renderAll();
    }
    this.commitDrawnSignature();
  }

  onDrawnEnd() {
    this.commitDrawnSignature();
  }

  commitTypedSignature() {
    if (this.readOnly || this.formatForPDF) {
      return;
    }
    const typed = (this.typedSignatureControl.value || '').trim();
    this.updateControlValue({
      signatureType: 'typed',
      typedSignature: typed || null,
      drawnSignature: null
    });
  }

  clearTypedSignature() {
    this.typedSignatureControl.setValue('');
  }

  private initializeCanvas() {
    if (!this.canvasEl || !this.canvasEl.nativeElement || this.canvas) {
      return;
    }
    this.canvas = new fabric.Canvas(this.canvasEl.nativeElement, {
      isDrawingMode: true,
      selection: false,
      backgroundColor: '#ffffff'
    });
    this.canvas.setWidth(this.CANVAS_WIDTH);
    this.canvas.setHeight(this.CANVAS_HEIGHT);
    this.canvas.freeDrawingBrush.width = 2;
    this.canvas.freeDrawingBrush.color = '#111111';
    this.canvas.on('path:created', () => this.onDrawnEnd());

    const existing = this.control && this.control.value;
    if (existing && existing.signatureType === 'drawn' && existing.drawnSignature) {
      try {
        const data =
          typeof existing.drawnSignature === 'string'
            ? JSON.parse(existing.drawnSignature)
            : existing.drawnSignature;
        this.canvas.loadFromJSON(data, () => {
          this.canvas.renderAll();
        });
      } catch (e) {
        // ignore corrupt signature payload
      }
    }
  }

  private updateControlValue(value: FormTemplateSignatureValue) {
    if (!this.control || this.readOnly) {
      return;
    }
    this.control.setValue(value);
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
    this.refreshDisplayFields(value);
  }

  /** Rasterize Fabric JSON to a PNG data URL for PDF export and clinic read-only view. */
  private rasterizeDrawnSignatureForDisplay(): Promise<void> | null {
    const signatureJson = this.displaySignatureData;
    if (!signatureJson) {
      return null;
    }

    return new Promise(resolve => {
      const canvas = new fabric.Canvas(document.createElement('canvas'), {
        width: this.CANVAS_WIDTH,
        height: this.CANVAS_HEIGHT,
        backgroundColor: '#fff',
        enableRetinaScaling: false
      });

      try {
        canvas.loadFromJSON(signatureJson, () => {
          canvas.getObjects().forEach(obj => {
            obj.selectable = false;
            obj.evented = false;
            if (obj.type === 'path' && !obj.stroke) {
              obj.set('stroke', '#000');
            }
          });
          canvas.renderAll();
          this.drawnSignatureDataUrl = canvas.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: 2
          });
          canvas.dispose();
          this.changeDetector.detectChanges();
          resolve();
        });
      } catch (e) {
        canvas.dispose();
        resolve();
      }
    });
  }
}
